import React, { useMemo, useState } from "react";
import { Plus, PenSquare, Trash2, Check, X } from "lucide-react";
import { Modal } from "../Modal";
import { StatusBadge } from "../Badges";
import api, { apiErr } from "../../lib/api";
import { toast } from "sonner";

const NEW_QUOTE = "__new__";

function SectionLabel({ children }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: ".08em", textTransform: "uppercase",
      color: "var(--text-3)", marginBottom: 6 }}>{children}</div>
  );
}

/**
 * SPT-PTE-2026-0071 — manage the Sage quotes on ONE delivery note.
 *  - Add a quote to the items it covers (mode "add": items not yet on a quote
 *    are pre-ticked, target = new quote number).
 *  - Edit (mode "edit"): move ticked items to another / a new quote, rename a
 *    quote on all its items (typo fix), take a quote off its items, or clear
 *    the quote from ticked items ("Not yet quoted").
 * Built for the backlog too: DNs created before this change carry the FIRST
 * quote on every line, so staff tick the items that really belong to the
 * 2nd/3rd quote and move them. The dialog stays open after each change so a
 * whole DN can be sorted in one go.
 * Every change goes through /job-items/bulk-quote-po (same per-line audit
 * trail + PO follow-up countdown as a per-line edit), so the previous quote
 * number is always kept in each item's history.
 */
export function QuoteManagerModal({ jobId, headerQuote, items, mode = "add", onClose, onSaved }) {
  const quotes = useMemo(() => [...new Set(items.map((i) => i.quote_number).filter(Boolean))], [items]);
  const countOf = (q) => items.filter((i) => (i.quote_number || "") === q).length;
  const unquoted = items.filter((i) => !i.quote_number);
  const [picked, setPicked] = useState(() => new Set(mode === "add" ? unquoted.map((i) => i.id) : []));
  const [target, setTarget] = useState(mode === "add" || quotes.length === 0 ? NEW_QUOTE : "");
  const [newQuote, setNewQuote] = useState("");
  const [renaming, setRenaming] = useState(null); // { from, to }
  const [removing, setRemoving] = useState(null); // quote number awaiting confirm
  const [busy, setBusy] = useState(false);

  const targetQuote = target === NEW_QUOTE ? newQuote.trim() : target;
  const pickedItems = items.filter((i) => picked.has(i.id));
  const moving = pickedItems.filter((i) => i.quote_number && i.quote_number !== targetQuote);
  const ordered = [...unquoted, ...quotes.flatMap((q) => items.filter((i) => i.quote_number === q))];

  const toggle = (id) => setPicked((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const tickGroup = (q) => setPicked((s) => {
    const ids = items.filter((i) => (i.quote_number || "") === q).map((i) => i.id);
    const n = new Set(s);
    const all = ids.length > 0 && ids.every((id) => n.has(id));
    ids.forEach((id) => (all ? n.delete(id) : n.add(id)));
    return n;
  });

  const save = async (ids, quoteNumber) => {
    const r = await api.post("/job-items/bulk-quote-po", { item_ids: ids, quote_number: quoteNumber });
    if (r.data.skipped?.length) toast.warning(`${r.data.skipped.length} item(s) could not be changed`);
    return r.data;
  };
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); if (onSaved) await onSaved(); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const assign = () => {
    if (!targetQuote) { toast.error("Choose a quote or type a new quote number."); return; }
    if (!picked.size) { toast.error("Tick at least one item."); return; }
    const q = targetQuote;
    run(async () => {
      const d = await save([...picked], q);
      toast.success(`${d.count} item(s) now on quote ${q}`);
      setPicked(new Set());
      setNewQuote("");
      setTarget(q);
    });
  };
  const clearTicked = () => {
    const ids = pickedItems.filter((i) => i.quote_number).map((i) => i.id);
    if (!ids.length) return;
    run(async () => {
      const d = await save(ids, "");
      toast.success(`Quote taken off ${d.count} item(s) — now "Not yet quoted"`);
      setPicked(new Set());
    });
  };
  const doRename = () => {
    const from = renaming.from;
    const to = (renaming.to || "").trim();
    if (!to || to === from) { setRenaming(null); return; }
    run(async () => {
      const ids = items.filter((i) => i.quote_number === from).map((i) => i.id);
      const d = await save(ids, to);
      // Keep the DN header reference in step when it was the same number.
      if (headerQuote && headerQuote === from) await api.patch(`/jobs/${jobId}/header`, { quote_number: to });
      toast.success(`Quote ${from} renamed to ${to} on ${d.count} item(s)`);
      setRenaming(null);
      if (target === from) setTarget(to);
    });
  };
  const doRemove = (q) => run(async () => {
    const ids = items.filter((i) => i.quote_number === q).map((i) => i.id);
    const d = await save(ids, "");
    toast.success(`Quote ${q} taken off ${d.count} item(s)`);
    setRemoving(null);
    if (target === q) setTarget("");
  });

  return (
    <Modal open onClose={onClose} icon={mode === "add" ? Plus : PenSquare} wide
      title={mode === "add" ? "Add quote to items" : "Edit quotes on this delivery note"}
      subtitle={mode === "add"
        ? "Type the quote number and tick the items it covers. Items not yet on a quote are ticked for you."
        : "Move items between quotes, rename a quote, or take a quote off items."}
      footer={<button className="btn ghost" onClick={onClose} disabled={busy} data-testid="quotes-done">Done</button>}>

      {quotes.length > 0 && (
        <div style={{ marginBottom: 14 }} data-testid="quotes-list">
          <SectionLabel>Quotes on this delivery note</SectionLabel>
          {quotes.map((q) => (
            <div key={q} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0",
              borderBottom: "1px solid var(--line)", flexWrap: "wrap" }} data-testid={`quotes-row-${q}`}>
              {renaming?.from === q ? (
                <>
                  <input className="input mono" style={{ maxWidth: 220 }} autoFocus value={renaming.to}
                    onChange={(e) => setRenaming({ from: q, to: e.target.value })}
                    data-testid={`quotes-rename-input-${q}`} />
                  <button className="btn sm gold" onClick={doRename} disabled={busy} data-testid={`quotes-rename-save-${q}`}>
                    <Check size={13} /> Save
                  </button>
                  <button className="btn sm ghost" onClick={() => setRenaming(null)} disabled={busy}>
                    <X size={13} /> Cancel
                  </button>
                  <span className="t-mut" style={{ fontSize: 11.5 }}>Changes the number on all {countOf(q)} item(s).</span>
                </>
              ) : removing === q ? (
                <>
                  <span style={{ fontSize: 12.5 }}>Take <b className="mono">{q}</b> off its {countOf(q)} item(s)? They go back to "Not yet quoted".</span>
                  <button className="btn sm gold" onClick={() => doRemove(q)} disabled={busy} data-testid={`quotes-remove-yes-${q}`}>Yes, take it off</button>
                  <button className="btn sm ghost" onClick={() => setRemoving(null)} disabled={busy}>No</button>
                </>
              ) : (
                <>
                  <span className="mono t-strong">{q}</span>
                  <span className="t-mut" style={{ fontSize: 12 }}>{countOf(q)} item(s)</span>
                  <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
                    <button className="btn sm ghost" onClick={() => { setRemoving(null); setRenaming({ from: q, to: q }); }}
                      disabled={busy} data-testid={`quotes-rename-${q}`}><PenSquare size={12} /> Rename</button>
                    <button className="btn sm ghost" onClick={() => { setRenaming(null); setRemoving(q); }}
                      disabled={busy} data-testid={`quotes-remove-${q}`}><Trash2 size={12} /> Take off items</button>
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      <SectionLabel>Put the ticked items on a quote</SectionLabel>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <select className="input" style={{ maxWidth: 220 }} value={target}
          onChange={(e) => setTarget(e.target.value)} data-testid="quotes-target">
          <option value="">— Choose quote —</option>
          {quotes.map((q) => <option key={q} value={q}>{q}</option>)}
          <option value={NEW_QUOTE}>New quote number…</option>
        </select>
        {target === NEW_QUOTE && (
          <input className="input mono" style={{ maxWidth: 200 }} autoFocus value={newQuote}
            onChange={(e) => setNewQuote(e.target.value)} placeholder="e.g. QT-24791"
            data-testid="add-quote-number" />
        )}
        <button className="btn sm gold" onClick={assign} disabled={busy || !picked.size || !targetQuote}
          data-testid="add-quote-submit">
          <Check size={13} /> {busy ? "Saving…" : `Put ${picked.size} item(s) on ${targetQuote || "quote"}`}
        </button>
        {mode === "edit" && (
          <button className="btn sm ghost" onClick={clearTicked}
            disabled={busy || !pickedItems.some((i) => i.quote_number)} data-testid="quotes-clear-ticked">
            <X size={13} /> Take quote off ticked
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
        <span className="t-mut" style={{ fontSize: 12 }}>Tick all on:</span>
        {unquoted.length > 0 && (
          <button className="btn sm ghost" onClick={() => tickGroup("")} data-testid="add-quote-pick-unquoted">
            Not yet quoted ({unquoted.length})
          </button>
        )}
        {quotes.map((q) => (
          <button key={q} className="btn sm ghost" onClick={() => tickGroup(q)} data-testid={`quotes-tick-${q}`}>
            {q} ({countOf(q)})
          </button>
        ))}
        <button className="btn sm ghost" onClick={() => setPicked(new Set())} data-testid="add-quote-pick-none">Untick all</button>
        <span className="t-mut" style={{ fontSize: 12, marginLeft: "auto" }} data-testid="add-quote-count">
          {picked.size} of {items.length} ticked
        </span>
      </div>

      <div style={{ maxHeight: 340, overflowY: "auto", border: "1px solid var(--line)", borderRadius: 8 }}>
        <table className="dt" style={{ width: "100%" }}>
          <thead>
            <tr><th style={{ width: 30 }} /><th>Item</th><th>Serial / issue no.</th><th>Status</th><th>Current quote</th></tr>
          </thead>
          <tbody>
            {ordered.map((it) => (
              <tr key={it.id} onClick={() => toggle(it.id)} style={{ cursor: "pointer" }} data-testid={`add-quote-row-${it.id}`}>
                <td><input type="checkbox" checked={picked.has(it.id)} onChange={() => toggle(it.id)}
                  onClick={(e) => e.stopPropagation()} data-testid={`add-quote-check-${it.id}`} /></td>
                <td>{it.description || "—"}</td>
                <td className="mono">{it.serial_number || it.issue_number || "—"}</td>
                <td><StatusBadge status={it.status} /></td>
                <td className="mono">{it.quote_number || <span className="t-mut">Not yet quoted</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {moving.length > 0 && targetQuote && (
        <div className="card pad" style={{ background: "#FBF3D6", fontSize: 12, marginTop: 8 }}
          data-testid="add-quote-moving-warning">
          {moving.length} ticked item(s) are already on another quote and will move to {targetQuote}.
          The old quote number stays in each item's history.
        </div>
      )}
    </Modal>
  );
}

// Back-compat name (first version of this dialog).
export function AddQuoteModal(props) {
  return <QuoteManagerModal mode="add" {...props} />;
}
